import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const CorrelationMatrix = ({ data, assets }) => {
  const [selectedAsset, setSelectedAsset] = useState(null);

  const getCorrelationColor = (correlation) => {
    const abs = Math.abs(correlation);
    if (abs >= 0.8) return 'bg-error text-error-foreground';
    if (abs >= 0.6) return 'bg-warning text-warning-foreground';
    if (abs >= 0.4) return 'bg-accent text-accent-foreground';
    if (abs >= 0.2) return 'bg-secondary text-secondary-foreground';
    return 'bg-muted text-muted-foreground';
  };

  const getCorrelationValue = (asset1, asset2) => {
    if (asset1 === asset2) return 1.0;
    const key = `${asset1}-${asset2}`;
    const reverseKey = `${asset2}-${asset1}`;
    return data?.[key] || data?.[reverseKey] || 0;
  };

  const formatCorrelation = (value) => {
    return value?.toFixed(2);
  };

  const getHighCorrelations = () => {
    const highCorr = [];
    for (let i = 0; i < assets?.length; i++) {
      for (let j = i + 1; j < assets?.length; j++) {
        const corr = getCorrelationValue(assets?.[i], assets?.[j]);
        if (Math.abs(corr) >= 0.7) {
          highCorr?.push({
            asset1: assets?.[i],
            asset2: assets?.[j],
            correlation: corr
          });
        }
      }
    }
    return highCorr?.sort((a, b) => Math.abs(b?.correlation) - Math.abs(a?.correlation));
  };

  const highCorrelations = getHighCorrelations();

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground">Correlation Matrix</h3>
        <div className="flex items-center space-x-4 text-xs">
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-muted rounded"></div>
            <span>Low (&lt;0.2)</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-secondary rounded"></div>
            <span>Moderate (0.2-0.4)</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-warning rounded"></div>
            <span>High (0.6-0.8)</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-3 h-3 bg-error rounded"></div>
            <span>Very High (&gt;0.8)</span>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <div className="overflow-x-auto">
            <div className="inline-block min-w-full">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="w-20"></th>
                    {assets?.map((asset) => (
                      <th key={asset} className="w-16 p-1">
                        <div className="text-xs font-medium text-muted-foreground transform -rotate-45 origin-bottom-left">
                          {asset}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {assets?.map((rowAsset) => (
                    <tr key={rowAsset}>
                      <td className="p-1">
                        <div className="text-xs font-medium text-muted-foreground text-right pr-2">
                          {rowAsset}
                        </div>
                      </td>
                      {assets?.map((colAsset) => {
                        const correlation = getCorrelationValue(rowAsset, colAsset);
                        const isSelected = selectedAsset === rowAsset || selectedAsset === colAsset;
                        return (
                          <td key={colAsset} className="p-1">
                            <div
                              className={`w-12 h-8 flex items-center justify-center text-xs font-medium rounded cursor-pointer transition-all hover:scale-110 ${
                                getCorrelationColor(correlation)
                              } ${isSelected ? 'ring-2 ring-primary' : ''}`}
                              onClick={() => setSelectedAsset(selectedAsset === rowAsset ? null : rowAsset)}
                              title={`${rowAsset} vs ${colAsset}: ${formatCorrelation(correlation)}`}
                            >
                              {formatCorrelation(correlation)}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3 flex items-center">
              <Icon name="AlertTriangle" size={16} className="mr-2 text-warning" />
              High Correlations
            </h4>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {highCorrelations?.length > 0 ? (
                highCorrelations?.map((item, index) => (
                  <div key={index} className="p-3 bg-muted rounded-lg">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-medium text-foreground">
                        {item?.asset1} × {item?.asset2}
                      </div>
                      <div className={`text-sm font-bold ${
                        Math.abs(item?.correlation) >= 0.8 ? 'text-error' : 'text-warning'
                      }`}>
                        {formatCorrelation(item?.correlation)}
                      </div>
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      {Math.abs(item?.correlation) >= 0.8 ? 'Very High Risk' : 'High Risk'}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-sm text-muted-foreground text-center py-4">
                  No high correlations detected
                </div>
              )}
            </div>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-foreground mb-3">Diversification Score</h4>
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm text-muted-foreground">Overall Score</span>
                <span className="text-lg font-bold text-success">
                  {Math.round((1 - highCorrelations?.length / (assets?.length * (assets?.length - 1) / 2)) * 100)}%
                </span>
              </div>
              <div className="w-full bg-border rounded-full h-2">
                <div 
                  className="bg-success h-2 rounded-full transition-all"
                  style={{ 
                    width: `${Math.round((1 - highCorrelations?.length / (assets?.length * (assets?.length - 1) / 2)) * 100)}%` 
                  }}
                ></div>
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                Based on correlation analysis
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CorrelationMatrix;